import { useState, useRef, useEffect } from 'react';
import { zoekIcpc } from '@/lib/ai';
import { Search, Loader2, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface IcpcResult {
  code: string;
  description: string;
  chapter: string | null;
}

interface IcpcSearchBarProps {
  selectedCode: string | null | undefined;
  onSelect: (code: string, description: string) => void;
}

export function IcpcSearchBar({ selectedCode, onSelect }: IcpcSearchBarProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<IcpcResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      /**
       * Het onderscheid tussen code- en tekstzoeken staat nu op de server, in
       * GET /api/icpc/search. Dat is niet alleen een verhuizing: dit component
       * stelde zijn eigen query op tegen de tabel -- `ilike` met een patroon dat
       * uit het invoerveld kwam -- en bepaalde zelf welke kolommen het terugkreeg.
       */
      try {
        const gevonden = await zoekIcpc(query.trim(), 15);
        setResults(gevonden);
        setOpen(true);
      } catch {
        // Een mislukte zoekopdracht laat de lijst leeg; een foutmelding bij het
        // typen zou meer storen dan helpen.
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative mt-1">
      <div className="relative">
        <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder="Zoek ICPC code of omschrijving..."
          className="w-full h-7 pl-6 pr-6 text-[11px] rounded border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        />
        {searching && (
          <Loader2 className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3 animate-spin text-muted-foreground" />
        )}
      </div>

      {open && results.length > 0 && (
        <div className="z-50 mt-0.5 w-full max-h-36 overflow-auto rounded border border-border bg-popover shadow-md">
          {results.map((r) => {
            const isSelected = selectedCode === r.code;
            return (
              <button
                key={r.code}
                type="button"
                onClick={() => {
                  onSelect(r.code, r.description);
                  setQuery('');
                  setOpen(false);
                }}
                className={`flex items-center gap-1.5 w-full px-2 py-1 text-[11px] text-left transition-colors ${
                  isSelected
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground hover:bg-accent'
                }`}
              >
                <span className="font-bold shrink-0">{r.code}</span>
                <span className="truncate">{r.description}</span>
                {isSelected && <Check className="h-2.5 w-2.5 ml-auto shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
